/**
 * @file CustomDatePicker_V2.js
 * @description A custom date picker component that allows the user to select a date and provides a custom header with navigation buttons.
 * @component
 *
 * @param {string} selectedDate - The initial selected date
 * @param {function} onDateChange - Callback function to handle date changes
 * @param {string} displayFormat - date-fns format string for the input display value (default: 'MM/dd/yyyy')
 * @param {boolean} disablePastDates - Whether to disable selection of past dates
 * @param {boolean} disableFutureDates - Whether to disable selection of future dates
 * @param {Date} minDate - Minimum selectable date
 * @param {Date} maxDate - Maximum selectable date
 * @param {string} inputClassName - Additional class name applied to the input element.
 */

import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import {
    getMonth,
    getYear,
    parseISO,
    format,
    isValid,
    parse,
    startOfDay,
    isBefore,
    isAfter,
} from 'date-fns';
import range from 'lodash/range';
import 'react-datepicker/dist/react-datepicker.css';
import './CustomDatePicker.css';
import { getText } from '../../i18n';

const CustomDatePicker = ({
    onDateChange,
    selectedDate,
    displayFormat = 'MM/dd/yyyy',
    errorMessage,
    hideErrorMessage = false,
    disabled = false,
    disablePastDates = false,
    disableFutureDates = false,
    minDate = null,
    maxDate = null,
    isActive = true,
    inputClassName = '',
}) => {
    const [startDate, setStartDate] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [committedDate, setCommittedDate] = useState(null);
    const acceptedDateFormats =
        displayFormat === 'MM/dd/yyyy'
            ? ['MM/dd/yyyy']
            : [displayFormat, 'MM/dd/yyyy'];

    const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    useEffect(() => {
        if (selectedDate && isActive) {
            const parsedDate =
                selectedDate instanceof Date
                    ? selectedDate
                    : /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)
                      ? parse(selectedDate, 'yyyy-MM-dd', new Date())
                      : parseISO(selectedDate);
            if (isValid(parsedDate)) {
                setStartDate(parsedDate);
                setCommittedDate(parsedDate);
                setInputValue(format(parsedDate, displayFormat));
            }
        } else {
            setStartDate(null);
            setCommittedDate(null);
            setInputValue('');
        }
    }, [selectedDate, isActive]);

    const handleDateChange = date => {
        setStartDate(date);
        if (date) {
            setCommittedDate(date);
            const formattedDate = format(date, 'yyyy-MM-dd');
            setInputValue(format(date, displayFormat));
            onDateChange(formattedDate);
        } else {
            setCommittedDate(null);
            setInputValue('');
            onDateChange(null);
        }
    };

    const normalizeSlashDateInput = value => {
        const digits = value.replace(/\D/g, '').slice(0, 8);
        if (digits.length <= 2) {
            return digits;
        }
        if (digits.length <= 4) {
            return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    };

    const updateCalendarPreviewFromSlashInput = normalizedValue => {
        const digits = normalizedValue.replace(/\D/g, '');
        if (digits.length < 2) {
            return;
        }

        const month = Math.min(
            Math.max(parseInt(digits.slice(0, 2), 10), 1),
            12,
        );
        const day =
            digits.length >= 4
                ? Math.min(Math.max(parseInt(digits.slice(2, 4), 10), 1), 31)
                : 1;
        const year =
            digits.length >= 8
                ? parseInt(digits.slice(4, 8), 10)
                : new Date().getFullYear();

        const previewDate = new Date(year, month - 1, day);
        if (isValid(previewDate)) {
            setStartDate(previewDate);
        }
    };

    const getEffectiveMinDate = () => {
        if (disablePastDates) {
            return startOfDay(new Date());
        }
        return minDate ? startOfDay(minDate) : null;
    };

    const getEffectiveMaxDate = () => {
        if (disableFutureDates) {
            return startOfDay(new Date());
        }
        return maxDate ? startOfDay(maxDate) : null;
    };

    const isDateWithinRange = date => {
        const normalizedDate = startOfDay(date);
        const effectiveMinDate = getEffectiveMinDate();
        const effectiveMaxDate = getEffectiveMaxDate();

        if (effectiveMinDate && isBefore(normalizedDate, effectiveMinDate)) {
            return false;
        }

        if (effectiveMaxDate && isAfter(normalizedDate, effectiveMaxDate)) {
            return false;
        }

        return true;
    };

    const parseCommittedInput = value => {
        for (const inputFormat of acceptedDateFormats) {
            const parsedDate = parse(value, inputFormat, new Date());
            if (
                isValid(parsedDate) &&
                format(parsedDate, inputFormat) === value
            ) {
                return parsedDate;
            }
        }

        return null;
    };

    const getCommittedDisplayValue = () => {
        if (!committedDate || !isValid(committedDate)) {
            return '';
        }

        return format(committedDate, displayFormat);
    };

    const handleInputCommit = event => {
        if (
            !event ||
            !event.target ||
            typeof event.target.value !== 'string' ||
            !isActive ||
            disabled
        ) {
            return;
        }

        const committedInput = event.target.value.trim();
        if (!committedInput) {
            setStartDate(null);
            setCommittedDate(null);
            setInputValue('');
            onDateChange(null);
            return;
        }

        const parsedCommittedDate = parseCommittedInput(committedInput);
        if (!parsedCommittedDate || !isDateWithinRange(parsedCommittedDate)) {
            const committedDisplayValue = getCommittedDisplayValue();
            setStartDate(committedDate);
            setInputValue(committedDisplayValue);
            event.target.value = committedDisplayValue;
            return;
        }

        setStartDate(parsedCommittedDate);
        setCommittedDate(parsedCommittedDate);
        setInputValue(format(parsedCommittedDate, displayFormat));
        onDateChange(format(parsedCommittedDate, 'yyyy-MM-dd'));
    };

    const generateYears = currentYear => {
        const startYear = minDate ? getYear(minDate) : currentYear - 125;
        const endYear = maxDate ? getYear(maxDate) : currentYear + 1;
        return range(startYear, endYear + 1, 1);
    };

    return (
        <>
            <DatePicker
                selected={startDate}
                preSelection={startDate || undefined}
                openToDate={startDate || undefined}
                onChange={handleDateChange}
                onBlur={handleInputCommit}
                value={inputValue}
                onChangeRaw={event => {
                    if (
                        !event ||
                        !event.target ||
                        typeof event.target.value !== 'string'
                    ) {
                        return;
                    }

                    if (!isActive || disabled) {
                        return;
                    }

                    if (displayFormat !== 'MM/dd/yyyy') {
                        const rawValue = event.target.value;
                        setInputValue(rawValue);

                        if (!rawValue) {
                            setStartDate(null);
                        }

                        return;
                    }

                    const normalizedValue = normalizeSlashDateInput(
                        event.target.value,
                    );

                    if (normalizedValue !== event.target.value) {
                        event.target.value = normalizedValue;
                    }

                    setInputValue(normalizedValue);

                    if (!normalizedValue) {
                        setStartDate(null);
                        return;
                    }

                    updateCalendarPreviewFromSlashInput(normalizedValue);
                }}
                customInput={
                    <input
                        type="text"
                        value={inputValue}
                        maxLength={displayFormat === 'MM/dd/yyyy' ? 10 : 30}
                        className={`form-control ${errorMessage ? 'is-invalid' : ''}${inputClassName ? ` ${inputClassName}` : ''}`}
                        disabled={disabled}
                        readOnly={!isActive}
                    />
                }
                disabled={disabled}
                dateFormat={acceptedDateFormats}
                formatWeekDay={nameOfDay => nameOfDay.substr(0, 3)}
                todayButton="Today"
                showIcon={!errorMessage}
                icon={
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="17"
                        viewBox="0 0 16 17"
                        fill="none"
                    >
                        <path
                            d="M8 7.60663C8.27614 7.60663 8.5 7.83049 8.5 8.10663V9.60663H10C10.2761 9.60663 10.5 9.83049 10.5 10.1066C10.5 10.3828 10.2761 10.6066 10 10.6066H8.5V12.1066C8.5 12.3828 8.27614 12.6066 8 12.6066C7.72386 12.6066 7.5 12.3828 7.5 12.1066V10.6066H6C5.72386 10.6066 5.5 10.3828 5.5 10.1066C5.5 9.83049 5.72386 9.60663 6 9.60663H7.5V8.10663C7.5 7.83049 7.72386 7.60663 8 7.60663Z"
                            fill="#82808E"
                        />
                        <path
                            d="M3.5 0.606628C3.77614 0.606628 4 0.830486 4 1.10663V1.60663H12V1.10663C12 0.830486 12.2239 0.606628 12.5 0.606628C12.7761 0.606628 13 0.830486 13 1.10663V1.60663H14C15.1046 1.60663 16 2.50206 16 3.60663V14.6066C16 15.7112 15.1046 16.6066 14 16.6066H2C0.895431 16.6066 0 15.7112 0 14.6066V3.60663C0 2.50206 0.895431 1.60663 2 1.60663H3V1.10663C3 0.830486 3.22386 0.606628 3.5 0.606628ZM1 4.60663V14.6066C1 15.1589 1.44772 15.6066 2 15.6066H14C14.5523 15.6066 15 15.1589 15 14.6066V4.60663H1Z"
                            fill="#82808E"
                        />
                    </svg>
                }
                placeholderText={getText('msg_49398473b903')}
                minDate={disablePastDates ? new Date() : minDate}
                maxDate={disableFutureDates ? new Date() : maxDate}
                renderCustomHeader={({
                    date,
                    changeMonth,
                    changeYear,
                    decreaseMonth,
                    increaseMonth,
                    increaseYear,
                    decreaseYear,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                }) => (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px',
                            gap: '10px',
                            borderBottom: '1px solid rgba(222, 226, 230, 1)',
                        }}
                    >
                        <i
                            className="bi bi-chevron-double-left"
                            style={{
                                color: prevMonthButtonDisabled
                                    ? 'rgba(206, 212, 218, 0.5)'
                                    : 'rgba(206, 212, 218, 1)',
                            }}
                            onClick={decreaseYear}
                            disabled={prevMonthButtonDisabled}
                        />
                        <i
                            className="bi bi-chevron-left"
                            style={{
                                color: prevMonthButtonDisabled
                                    ? 'rgba(206, 212, 218, 0.5)'
                                    : 'rgba(206, 212, 218, 1)',
                            }}
                            onClick={decreaseMonth}
                            disabled={prevMonthButtonDisabled}
                        />
                        <div
                            className="custom-select-wrapper"
                            style={{ marginLeft: 'auto' }}
                        >
                            <select
                                value={months[getMonth(date)]}
                                onChange={({ target: { value } }) =>
                                    changeMonth(months.indexOf(value))
                                }
                            >
                                {months.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div
                            className="custom-select-wrapper"
                            style={{ marginRight: 'auto' }}
                        >
                            <select
                                value={getYear(date)}
                                onChange={({ target: { value } }) =>
                                    changeYear(parseInt(value))
                                }
                            >
                                {generateYears(getYear(date)).map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <i
                            className="bi bi-chevron-right"
                            style={{
                                color: nextMonthButtonDisabled
                                    ? 'rgba(206, 212, 218, 0.5)'
                                    : 'rgba(206, 212, 218, 1)',
                            }}
                            onClick={increaseMonth}
                            disabled={nextMonthButtonDisabled}
                        />
                        <i
                            className="bi bi-chevron-double-right"
                            style={{
                                color: nextMonthButtonDisabled
                                    ? 'rgba(206, 212, 218, 0.5)'
                                    : 'rgba(206, 212, 218, 1)',
                            }}
                            onClick={increaseYear}
                            disabled={nextMonthButtonDisabled}
                        />
                    </div>
                )}
            />
            {errorMessage && !hideErrorMessage && (
                <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </>
    );
};

export default CustomDatePicker;
